import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import CustomTabs from "../../../components/CustomTabs";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/input";
import NewTableComponent from "../../../components/NewTableComponent";
import Select from "../../../components/Select";
import RFQComments from "../../../components/RFQComments";
import ConfirmDeleteModal from "../../../components/modals/ConfirmDeleteModal";
import "../../../styles/RFQDetails.css";
import { MdDeleteOutline } from "react-icons/md";
import { BsFileText } from "react-icons/bs";
import { Spinner } from "react-bootstrap";
import PermissionWrapper from '../../../components/PermissionWrapper';
import { AppMainModules, InventorySubModules, ProcurementPermission } from '../../../utils/app-constant';
import { useDispatch, useSelector } from "react-redux";
import { updatePartRFQ, resendRfq, fetchRFQDetail, getRFQBidsPaginated, createPurchaseOrder } from "../../../store/Slices/ProcurementSlice";

import { Formik } from "formik";
import ReactQuill from "react-quill-new";
import { formats, modules } from "../../../utils/helper";
import { fetchUsersDropdown, fetchInventoryPartsList } from "../../../store/Slices/dropdownSlice";
import moment from "moment";
import * as Yup from "yup";
import { successNotify, errorNotify } from "../../../utils/toast";
import CustomModal from "../../../components/CustomModal";
import { searchIcon } from "../../../utils/svgs";
import { RFQUpdateSchema } from "../../../utils/Schema";
import { getBidIdentifier, resolveBidPoActionState } from "./bidPoStatus";



const RFQDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("summary");
  const [attachments, setAttachments] = useState([]);
  const [bidPage, setBidPage] = useState(1);
  const [bidLimit, setBidLimit] = useState(10);
  const [showPartModal, setShowPartModal] = useState(false);
  const [partSearch, setPartSearch] = useState("");
  const [selectedPartIds, setSelectedPartIds] = useState([]);
  const [loadingPOs, setLoadingPOs] = useState({});
  const [generatedPoBidId, setGeneratedPoBidId] = useState(null);
  const [showResendConfirm, setShowResendConfirm] = useState(false);

  const { rfqDetail, rfqDetailLoading, updatePartRFQLoading, resendRfqLoading, getRFQBidsPaginated: bidsData, getRFQBidsLoading } = useSelector((state) => state.procurement);
  const { usersDropdownData, inventoryPartsListData, loading: dropdownLoading } = useSelector((state) => state.dropdowns);
  const rfqStatus = String(rfqDetail?.status || "").toLowerCase().trim();
  const isRfqClosed = rfqStatus === "closed" || rfqStatus === "close";
  // Backend stores this misspelled as "biding" - accept both spellings defensively.
  const isRfqBidding = rfqStatus === "biding" || rfqStatus === "bidding";
  const isSupplyChainApproved = String(rfqDetail?.supply_chain_status || "").toLowerCase().trim() === "approved";
  const canResend = isRfqBidding && isSupplyChainApproved;

  const handleResend = () => {
    dispatch(resendRfq(Number(id)))
      .unwrap()
      .then(() => {
        setShowResendConfirm(false);
        dispatch(fetchRFQDetail(id));
        successNotify("RFQ resent to vendors successfully");
      })
      .catch((err) => {
        errorNotify(err);
      });
  };
  const bidRows = useMemo(() => bidsData?.results || [], [bidsData]);
  const rfqDetailBids = useMemo(
    () => Array.isArray(rfqDetail?.bids) ? rfqDetail.bids : [],
    [rfqDetail?.bids]
  );
  const bidStatusSource = rfqDetailBids.length ? rfqDetailBids : bidRows;

  useEffect(() => {
    dispatch(fetchInventoryPartsList());
  }, [dispatch]);

  const partList = useMemo(() => {
    const parts = Array.isArray(inventoryPartsListData)
      ? [...inventoryPartsListData]
      : [];

    const getSortValue = (item) => {
      const rawDate =
        item?.created_at ||
        item?.createdAt ||
        item?.updated_at ||
        item?.updatedAt;
      const dateValue = new Date(rawDate || "").getTime();
      if (Number.isFinite(dateValue) && dateValue > 0) return dateValue;

      const rawId = item?.id ?? item?.part_id ?? item?.partId ?? 0;
      const numericId = Number(String(rawId).replace(/[^\d.-]/g, ""));
      return Number.isFinite(numericId) ? numericId : 0;
    };

    parts.sort((a, b) => getSortValue(b) - getSortValue(a));

    if (!partSearch) return parts;
    const lowerSearch = partSearch.toLowerCase();
    return parts.filter(item =>
      (item.name && item.name.toLowerCase().includes(lowerSearch)) ||
      (item.part_number && item.part_number.toLowerCase().includes(lowerSearch))
    );
  }, [inventoryPartsListData, partSearch]);

  const toggleSelect = (id) => {
    setSelectedPartIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (partList.length === 0) return;
    const allIds = partList.map((item) => item.id);
    const allSelected = allIds.every((id) => selectedPartIds.includes(id));
    if (allSelected) {
      setSelectedPartIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedPartIds((prev) => Array.from(new Set([...prev, ...allIds])));
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchRFQDetail(id));
    }
    dispatch(fetchUsersDropdown());
  }, [dispatch, id]);

  useEffect(() => {
    setGeneratedPoBidId(null);
  }, [id]);

  useEffect(() => {
    if (activeTab === 'bids' && id) {
      dispatch(getRFQBidsPaginated({
        parts_rfq_id: id,
        page: bidPage,
        limit: bidLimit
      }));
    }
  }, [dispatch, activeTab, id, bidPage, bidLimit]);

  const requiredStar = <span className="required_star" style={{ color: "red" }}>*</span>;

  const initialRecord = useMemo(() => {
    return {
      id: rfqDetail?.id || "",
      parts_rfq_id: rfqDetail?.parts_rfq_id || "",
      requester: rfqDetail?.requester ? { label: rfqDetail.requester.username, value: rfqDetail.requester.id, image: rfqDetail.requester.image, showIcon: true } : null,
      target_delivery_date: rfqDetail?.target_delivery_date ? moment(rfqDetail.target_delivery_date).format("YYYY-MM-DD") : "",
      notes: rfqDetail?.notes || "",
      items: rfqDetail?.items?.map(item => ({
        id: item.id,
        part_id: item.part_id,
        name: item.part?.name,
        quantity: item.quantity,
        description: item.part?.description,
      })) || [],
      files: rfqDetail?.files || []
    };
  }, [rfqDetail]);

  const [formInitialValues, setFormInitialValues] = useState(initialRecord);

  useEffect(() => {
    if (rfqDetail) {
      setFormInitialValues(initialRecord);
      setAttachments((rfqDetail.files || []).map(f => {
        if (typeof f === 'string') {
          return { fileName: f, originalName: f };
        }
        const name = f.fileName || f.file_name || f.file_path || f.url || f.path || f.file || f.image || '';
        return {
          ...f,
          fileName: name,
          originalName: f.originalName || f.original_name || name
        };
      }));
    }
  }, [rfqDetail, initialRecord]);

  const requesterOptions = useMemo(() => {
    return (usersDropdownData || []).map((user) => {
      const option = { ...user, showIcon: true };
      if (!option.label) option.label = user.username || user.name || user.email || "Unknown";
      if (!option.value) option.value = user.id;
      return option;
    });
  }, [usersDropdownData]);

  const rfqTabs = [
    {
      key: "summary",
      label: "Request Summary & Attachments",
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 4H11" stroke="#1447B1" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M5 8H11" stroke="#1447B1" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M5 12H9" stroke="#1447B1" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      key: "bids",
      label: "Bids Received",
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 6.5L8 3.5L13 6.5L8 9.5L3 6.5Z" stroke="#4D4D4D" strokeWidth="1.2" />
          <path d="M3 6.5V12.5L8 15.5L13 12.5V6.5" stroke="#4D4D4D" strokeWidth="1.2" />
          <path d="M8 9.5V15.5" stroke="#4D4D4D" strokeWidth="1.2" />
        </svg>
      ),
    },
  ];

  const handleGeneratePO = (bid) => {
    setLoadingPOs(prev => ({ ...prev, [bid.id]: true }));
    const payload = {
      parts_rfq_id: bid.parts_rfq_id,
      vendor_id: bid.vendor_id,
      rfq_vendor_quote_id: bid.id
    };

    dispatch(createPurchaseOrder(payload))
      .unwrap()
      .then(() => {
        successNotify("Purchase Order Generated Successfully");
        setGeneratedPoBidId(getBidIdentifier(bid));
        if (id) {
          dispatch(fetchRFQDetail(id));
          dispatch(getRFQBidsPaginated({
            parts_rfq_id: id,
            page: bidPage,
            limit: bidLimit
          }));
        }
      })
      .catch((err) => {
        errorNotify(err);
      })
      .finally(() => {
        setLoadingPOs(prev => ({ ...prev, [bid.id]: false }));
      });
  };

  const bidColumns = useMemo(() => [
    {
      name: "Vendor Name",
      selector: (row) => row.vendor?.name || "-",
      sortable: false,
      minWidth: "180px"
    },
    {
      name: "Delivery Type",
      selector: (row) => row.delivery_type || "-",
      sortable: false,
      cell: (row) => <span style={{ textTransform: 'capitalize' }}>{row.delivery_type}</span>,
      minWidth: "160px"
    },
    {
      name: "Delivery Date",
      selector: (row) => row.delivery_date,
      format: (row) => row.delivery_date ? moment(row.delivery_date).format("YYYY-MM-DD") : "-",
      sortable: false,
      minWidth: "160px"
    },
    {
      name: "Discount",
      selector: (row) => row.total_discount,
      cell: (row) => parseFloat(row.total_discount) > 0 ? `${parseFloat(row.total_discount)}%` : "0%",
      minWidth: "120px"
    },
    {
      name: "Freight (PKR)",
      selector: (row) => row.total_shipping,
      format: (row) => parseFloat(row.total_shipping || 0).toLocaleString(),
      minWidth: "150px"
    },
    {
      name: "Taxes (PKR)",
      selector: (row) => row.total_tax,
      format: (row) => parseFloat(row.total_tax || 0).toLocaleString(),
      minWidth: "150px"
    },
    {
      name: "Total Quote Amount",
      selector: (row) => row.total_amount,
      format: (row) => parseFloat(row.total_amount || 0).toLocaleString(),
      sortable: false,
      minWidth: "200px"
    },
    {
      name: "Total Quotes",
      selector: (row) => row.total_quotes || "-",
      minWidth: "140px"
    },
    {
      name: "Generate PO",
      cell: (row) => {
        const matchedDetailBid = rfqDetailBids.find((detailBid) => {
          const detailBidIdentifier = getBidIdentifier(detailBid);
          const rowBidIdentifier = getBidIdentifier(row);

          if (detailBidIdentifier && rowBidIdentifier) {
            return detailBidIdentifier === rowBidIdentifier;
          }

          return String(detailBid?.id ?? "") === String(row?.id ?? "");
        });
        const bidActionRow = matchedDetailBid ? { ...row, ...matchedDetailBid } : row;
        const actionState = resolveBidPoActionState({
          bid: bidActionRow,
          bids: bidStatusSource,
          rfqPoGenerated: rfqDetail?.po_generated,
          generatedBidId: generatedPoBidId,
          container: rfqDetail,
        });
        const isLoading = Boolean(loadingPOs[row.id]);
        const isDisabled = isLoading || actionState.disabled;
        const actionColor = actionState.intent === "dismissed" ? "#64748B" : "#4014B8";

        return (
          <PermissionWrapper
            module={InventorySubModules.Procurement}
            permission={ProcurementPermission.CreatePo}
            mainModule={AppMainModules.inventoryManagement}
          >
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                color: actionColor,
                fontSize: '13px',
                fontWeight: 500,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.7 : 1,
                whiteSpace: 'nowrap'
              }}
              onClick={() => handleGeneratePO(row)}
              disabled={isDisabled}
            >
              {isLoading ? <Spinner size="sm" animation="border" role="status" /> : <BsFileText />}
              {isLoading ? 'Generating...' : actionState.label}
            </button>
          </PermissionWrapper>
        );
      },
      minWidth: "160px",
      right: true
    }
  ], [bidStatusSource, generatedPoBidId, loadingPOs, rfqDetail, rfqDetailBids]);

  // Placeholder for schema if it was here


  const handleFormSubmit = async (values, { setSubmitting }) => {
    if (isRfqClosed) {
      setSubmitting(false);
      return;
    }

    const updatedKeys = [];
    if (String(values.notes || "").trim() !== String(initialRecord.notes || "").trim()) updatedKeys.push("notes");
    if (values.target_delivery_date !== initialRecord.target_delivery_date) updatedKeys.push("target_delivery_date");

    const currentFiles = attachments.map(f => f.fileName || f.name).filter(Boolean);
    const initialFiles = (initialRecord.files || []).map(f => typeof f === 'string' ? f : (f.fileName || f.name)).filter(Boolean);

    const isFilesChanged = JSON.stringify(currentFiles.sort()) !== JSON.stringify(initialFiles.sort());
    if (isFilesChanged) updatedKeys.push("files");

    const itemsPayload = [];

    // Process CURRENT items
    values.items.forEach(item => {
      if (!item.id) {
        itemsPayload.push({
          part_id: item.part_id,
          quantity: Number(item.quantity),
          action: 'create'
        });
      } else {
        const originalItem = rfqDetail?.items?.find(i => i.id === item.id);
        const isQtyChanged = originalItem ? Number(originalItem.quantity) !== Number(item.quantity) : true;

        itemsPayload.push({
          id: item.id,
          part_id: item.part_id,
          quantity: Number(item.quantity),
          action: isQtyChanged ? 'update' : 'none'
        });
      }
    });

    // Process DELETED items
    if (rfqDetail?.items) {
      const deletedItems = rfqDetail.items.filter(orig => {
        const stillPresent = values.items.some(curr => String(curr.id) === String(orig.id));
        return !stillPresent;
      });

      deletedItems.forEach(item => {
        itemsPayload.push({
          id: item.id,
          part_id: item.part_id,
          quantity: Number(item.quantity || 0),
          action: 'delete'
        });
      });
    }

    const payload = {
      part_rfq_id: Number(rfqDetail.id),
      target_delivery_date: values.target_delivery_date,
      notes: values.notes,
      updatedKeys: updatedKeys,
      items: itemsPayload,
      files: currentFiles.filter(f => !initialFiles.includes(f))
    };

    try {
      await dispatch(updatePartRFQ(payload)).unwrap();
      successNotify("RFQ Updated Successfully");
      navigate('/procurement');
    } catch (e) {
      if (Array.isArray(e)) {
        errorNotify(e[0]);
      } else {
        errorNotify(e);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={formInitialValues}
      validationSchema={RFQUpdateSchema}
      onSubmit={handleFormSubmit}
    >
      {({ values, errors, touched, handleChange, setFieldValue, handleSubmit, submitCount }) => {
        const addedPartIds = new Set(values.items.map((item) => item.part_id));
        const modalSelectedParts = [];
        const modalUnselectedParts = [];
        partList.forEach((item) => {
          if (addedPartIds.has(item.id)) {
            modalSelectedParts.push(item);
            return;
          }
          modalUnselectedParts.push(item);
        });
        const modalPartList = [...modalSelectedParts, ...modalUnselectedParts];

        return (
          <>
          <Breadcrumbs values={["Procurement", "RFQ Details", "Request Summary & Attachments"]} />

          <div className="page_header">
            <h2>RFQ Details</h2>
            <div className="d-flex align-items-center gap-2">
              <PermissionWrapper
                module={InventorySubModules.Procurement}
                permission={ProcurementPermission.UpdateRfq}
                mainModule={AppMainModules.inventoryManagement}
              >
                <Button
                  cancelBtn={() => navigate(-1)}
                  submitText="Save Details"
                  type="button"
                  shouldRow={false}
                  isShowSubmitBtn={true}
                  submitBtn={handleSubmit}
                  loading={updatePartRFQLoading}
                  disabled={updatePartRFQLoading || isRfqClosed}
                />
              </PermissionWrapper>
            </div>
          </div>

          <div className="page_container rfq_details_page">
            <div className="rfq_detail_bar">
              <span>ID: <strong>{values.parts_rfq_id || "--"}</strong></span>
            </div>

            <Row className="rfq_details_row">
              <Col md={8}>
                <div className="rfq_detail_tabs">
                  <CustomTabs tabs={rfqTabs} activeTab={activeTab} setActiveTab={setActiveTab} />
                </div>

                {activeTab === "summary" && (
                  <>
                    <div className="content_box">
                      <div className="d-flex align-items-center justify-content-between">
                        <h6>Part & Request Information</h6>
                        <PermissionWrapper
                          module={InventorySubModules.Procurement}
                          permission={ProcurementPermission.UpdateRfq}
                          mainModule={AppMainModules.inventoryManagement}
                        >
                          <Button
                            submitText="Resend"
                            type="button"
                            shouldRow={false}
                            isShowCancel={false}
                            submitBtn={() => setShowResendConfirm(true)}
                            disabled={!canResend}
                          />
                        </PermissionWrapper>
                      </div>
                      <p>Main information for this part record.</p>

                      <div className="rfq_parts_table">
                        <div className="rfq_parts_header">
                          <span>Part Name</span>
                          <span>Quantity</span>
                          <span>Action</span>
                        </div>
                        {values.items.map((part, index) => {
                          const itemError = errors.items && errors.items[index] && errors.items[index].quantity;
                          const itemTouched = touched.items && touched.items[index] && touched.items[index].quantity;
                          const showItemError = Boolean(itemError && (itemTouched || submitCount > 0));

                          return (
                            <div className="rfq_parts_row" key={part.id || index}>
                              <span>{part.name}</span>
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <input
                                  type="number"
                                  value={part.quantity}
                                  onChange={(e) => setFieldValue(`items[${index}].quantity`, e.target.value)}
                                  disabled={isRfqClosed}
                                  placeholder="Enter Qty"
                                  onFocus={(e) => e.target.select()}
                                  style={{
                                    borderColor: showItemError ? 'red' : undefined
                                  }}
                                // readOnly={rfqDetail?.status !== 'draft'} 
                                />
                                {showItemError && (
                                  <span style={{ color: 'red', fontSize: '10px' }}>{itemError}</span>
                                )}
                              </div>
                              <PermissionWrapper
                                module={InventorySubModules.Procurement}
                                permission={ProcurementPermission.DeleteRfq}
                                mainModule={AppMainModules.inventoryManagement}
                              >
                                {/* Only show delete if Draft? */}
                                <button
                                  type="button"
                                  className="rfq_delete_btn"
                                  disabled={isRfqClosed}
                                  style={isRfqClosed ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                                  onClick={() => {
                                    if (isRfqClosed) return;
                                    const newItems = [...values.items];
                                    newItems.splice(index, 1);
                                    setFieldValue("items", newItems);
                                  }}
                                >
                                  <MdDeleteOutline />
                                </button>
                              </PermissionWrapper>
                            </div>
                          );
                        })}
                        <PermissionWrapper
                          module={InventorySubModules.Procurement}
                          permission={ProcurementPermission.UpdateRfq}
                          mainModule={AppMainModules.inventoryManagement}
                        >
                          <button
                            type="button"
                            className="rfq_add_part"
                            disabled={isRfqClosed}
                            style={isRfqClosed ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                            // disabled={rfqDetail?.status?.toLowerCase() !== 'draft'}
                            onClick={() => {
                              if (isRfqClosed) return;
                              setSelectedPartIds(values.items.map(item => item.part_id));
                              setShowPartModal(true);
                            }}
                          >
                            <span className="rfq_add_part_inner">
                              <span className="rfq_add_part_icon">+</span>
                              Add Part
                            </span>
                          </button>
                        </PermissionWrapper>
                        <div style={{ color: 'red', fontSize: '12px', marginTop: '5px', marginLeft: '5px' }}>
                          {typeof errors.items === 'string' && submitCount > 0 ? errors.items : null}
                        </div>
                      </div>

                      <Row className="mt-3">
                        <Col md={6}>
                          <Select
                            label={<>Requester {requiredStar}</>}
                            placeholder="Select Requester"
                            options={requesterOptions}
                            value={values.requester}
                            onChange={(val) => setFieldValue("requester", val)}
                            isDisabled={true}
                            error={errors.requester}
                            touched={touched.requester}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label={<>Target Delivery Date</>}
                            placeholder="YY-MM-DD"
                            type="date"
                            name="target_delivery_date"
                            value={values.target_delivery_date}
                            onChange={handleChange}
                            disabled={rfqDetail?.status?.toLowerCase() !== 'draft'}
                            error={errors.target_delivery_date}
                            touched={touched.target_delivery_date}
                          />
                        </Col>
                      </Row>
                    </div>

                    <div className="content_box">
                      <h6>Request Documents</h6>
                      <p>Upload relevant photos or documents for this RFQ.</p>
                      <div className="issue_file_upload_wrapper">
                        <label>Attachments</label>
                        <FileUpload
                          destination="rfq-attachments"
                          files={attachments}
                          setFiles={setAttachments}
                          maxFileSize={10}
                          className="issue_file_upload"
                          disabled={isRfqClosed}
                        />
                      </div>
                    </div>
                  </>
                )}

                {activeTab === "bids" && (
                  <div className="content_box">
                    <h6>Bid Information</h6>
                    <p>Details of bids received from vendors.</p>
                    <div className="rfq_bids_table">
                      <NewTableComponent
                        columns={bidColumns}
                        data={bidRows}
                        showPagination={true}
                        totalItems={bidsData?.totalItems || 0}
                        totalPages={bidsData?.totalPages || 0}
                        currentPage={bidPage}
                        setCurrentPage={setBidPage}
                        limit={bidLimit}
                        setLimit={setBidLimit}
                        loading={getRFQBidsLoading}
                        showFilter={false}
                      />
                    </div>
                  </div>
                )}
              </Col>

              <Col md={4}>
                <div className="rfq_right_card rfq_comments_card">
                  <RFQComments />
                </div>

                <div className="service_cost_summary_details rfq_right_card">
                  <div className="parts_general_notes">
                    <h5>General Notes</h5>
                    <p>Add any additional notes or observations here.</p>
                    {/* Using ReactQuill for Notes as per AddNewRFQ style if desired, or Textarea */}
                    <ReactQuill
                      placeholder={"write notes here..."}
                      modules={isRfqClosed ? { toolbar: false } : modules}
                      theme="snow"
                      value={values.notes}
                      onChange={(val) => setFieldValue("notes", val)}
                      formats={formats}
                      className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                      // readOnly={rfqDetail?.status?.toLowerCase() !== 'draft'}
                      readOnly={isRfqClosed}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          <CustomModal
            show={showPartModal}
            handleClose={() => {
              setShowPartModal(false);
              setPartSearch("");
              setSelectedPartIds([]);
            }}
            heading="Add Part"
            size="md"
            customHeaderCss="add_part_modal_header"
          >
            <div className="add_part_modal_body">
              <p>Select the parts you want to request for quotation</p>

              <div className="add_part_search">
                {searchIcon}
                <input
                  type="text"
                  placeholder="Search"
                  value={partSearch}
                  onChange={(e) => setPartSearch(e.target.value)}
                />
              </div>

              <div className="add_part_list">
                <button
                  type="button"
                  className="add_part_select_all"
                  onClick={toggleSelectAll}
                >
                  <span
                    className={`add_part_checkbox ${partList.length > 0 &&
                      partList.every((item) => selectedPartIds.includes(item.id))
                      ? "checked"
                      : ""
                      }`}
                  />
                  Select All
                </button>

                <div className="add_part_items">
                  {dropdownLoading ? <p>Loading...</p> : modalPartList.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      className="add_part_item"
                      onClick={() => toggleSelect(item.id)}
                    >
                      <span
                        className={`add_part_checkbox ${selectedPartIds.includes(item.id) ? "checked" : ""
                          }`}
                      />
                      {item.name || item.part_number}
                    </button>
                  ))}
                </div>
              </div>

              <div className="add_part_actions">
                <Button
                  shouldRow={false}
                  cancelBtnText="Cancel"
                  submitText={`Add Parts (${selectedPartIds.length})`}
                  cancelBtn={() => {
                    setShowPartModal(false);
                    setPartSearch("");
                    setSelectedPartIds([]);
                  }}
                  submitBtn={() => {
                    const newParts = partList
                      .filter((p) => selectedPartIds.includes(p.id))
                      .map((p) => ({
                        part_id: p.id,
                        part_id: p.id,
                        name: p.name || p.part_number,
                        quantity: "",
                        placeholder: "Enter Quantity"
                      }));

                    // Filter out duplicates
                    const existingIds = values.items.map(i => i.part_id);
                    const filteredNew = newParts.filter(p => !existingIds.includes(p.part_id));

                    setFieldValue("items", [...filteredNew, ...values.items]);
                    setShowPartModal(false);
                    setPartSearch("");
                    setSelectedPartIds([]);
                  }}
                  isShowSubmitBtn={selectedPartIds.length > 0}
                />
              </div>
            </div>
          </CustomModal>

          <ConfirmDeleteModal
            show={showResendConfirm}
            onClose={() => setShowResendConfirm(false)}
            onConfirm={handleResend}
            loading={resendRfqLoading}
            title="Resend this RFQ to vendors?"
            description="This will notify all vendors again to submit their bids."
            confirmText="Resend"
            cancelText="Cancel"
          />
          </>
        );
      }}
    </Formik >
  );
};

export default RFQDetails;
